import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

const FIVE9_WIDGET_HIDE_STYLE_ID = "five9-widget-hide-style";

const ensureFive9WidgetHidden = () => {
  if (document.getElementById(FIVE9_WIDGET_HIDE_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = FIVE9_WIDGET_HIDE_STYLE_ID;
  style.textContent = `
    #five9-frame-full,
    #five9-frame,
    [id*="five9-frame"],
    [class*="five9"][class*="frame"],
    [class*="five9"][class*="container"],
    iframe[id*="five9"],
    iframe[src*="five9"],
    .five9-chat-button,
    #five9-maximize-button {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
  `;
  document.head.appendChild(style);
};

const showFive9Widget = () => {
  document.getElementById(FIVE9_WIDGET_HIDE_STYLE_ID)?.remove();
};

const Five9Widget = ({ open, setOpen, isSpeed }) => {
  const { i18n } = useTranslation();
  const locale = i18n?.language === "enUS" ? "en-US" : i18n?.language;

  const enrolleeUser = JSON.parse(sessionStorage.getItem("enrolleeUser"));

  const [widgetLoaded, setWidgetLoaded] = useState(false);

  useEffect(() => {
    ensureFive9WidgetHidden();

    const script = document.createElement("script");
    script.src =
      "https://app.five9.com/consoles/SocialWidget/five9-social-widget.min.js";
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (!window.Five9SocialWidget) return;

      const options = isSpeed
        ? {
            rootUrl: "https://app.five9.com/consoles/",
            type: "chat",
            title: "LifeSafer ISA Sales Chat",
            tenant: "LMG Holdings",
            profiles: "ISA Sales Web Chat",
            showProfiles: true,
            autostart: false,
            theme:
              "https://www.lifesafer.com/wp-content/cache/perfmatters/www.lifesafer.com/minify/b5cf6a03b02c.theme.min.css?ver=1774300599",
            logo: "https://www.lifesaferisa.com/_next/image?url=%2Flifesaferisa-logo.png&w=384&q=75",
            surveyOptions: {
              showComment: true,
              requireComment: false,
            },
            fields: {
              name: {
                value: `${enrolleeUser?.FirstName || ""} ${enrolleeUser?.LastName || ""}`.trim(),
                show: false,
                label: "Name",
              },
              emailAddress: {
                value: enrolleeUser?.email,
                show: false,
                label: "",
              },
              ClientID: {
                value: enrolleeUser?.clientId,
                show: false,
                label: "Client ID",
                required: false,
              },
              Page: {
                value: enrolleeUser?.page,
                show: false,
                label: "Page",
                required: false,
              },
              UserLocale: {
                value: locale,
                show: false,
              },
            },
            playSoundOnMessage: true,
            allowCustomerToControlSoundPlay: false,
            showEmailButton: false,
            hideDuringAfterHours: false,
            useBusinessHours: false,
            showPrintButton: false,
            allowUsabilityMenu: false,
            enableCallback: false,
            callbackList: "",
            allowRequestLiveAgent: false,
          }
        : {
            rootUrl: "https://app.five9.com/consoles/",
            type: "chat",
            title: "LifeSafer Sales Chat",
            tenant: "LMG Holdings",
            profiles: "Sales Chat",
            showProfiles: true,
            autostart: false,
            theme:
              "https://www.lifesafer.com/wp-content/cache/perfmatters/www.lifesafer.com/css/front.used.css?ver=1774300975",
            logo: "https://www.lifesafer.com/wp-content/themes/lifesafer-2022/assets/img/lifesafer-logo-new.png.webp",
            surveyOptions: {
              showComment: true,
              requireComment: false,
            },
            fields: {
              name: {
                value: `${enrolleeUser?.FirstName || ""} ${enrolleeUser?.LastName || ""}`.trim(),
                show: false,
                label: "Name",
              },
              emailAddress: {
                value: enrolleeUser?.email,
                show: false,
                label: "",
              },
              ClientID: {
                value: enrolleeUser?.clientId,
                show: false,
                label: "Client ID",
                required: false,
              },
              Page: {
                value: enrolleeUser?.page,
                show: false,
                label: "Page",
                required: false,
              },
              UserLocale: {
                value: locale,
                show: false,
              },
            },
            playSoundOnMessage: true,
            allowCustomerToControlSoundPlay: false,
            showEmailButton: true,
            hideDuringAfterHours: false,
            useBusinessHours: false,
            showPrintButton: false,
            allowUsabilityMenu: false,
            enableCallback: false,
            callbackList: "",
            allowRequestLiveAgent: false,
          };

      window.Five9SocialWidget.addWidget(options);
      setWidgetLoaded(true);
    };

    return () => {
      showFive9Widget();
      document
        .querySelectorAll("[class*='five9'], [id*='five9']")
        .forEach((el) => el.remove());
    };
  }, [isSpeed, locale]);

  useEffect(() => {
    let minimizeBtn = null;
    const onMinimize = () => setOpen(false);

    const timer = window.setTimeout(() => {
      minimizeBtn =
        document.querySelector("#five9-minimize-button") ||
        document.querySelector("[class*='five9'][class*='minimize']");

      if (!minimizeBtn) return;

      minimizeBtn.addEventListener("click", onMinimize);
    }, 1000);

    return () => {
      window.clearTimeout(timer);
      if (minimizeBtn) {
        minimizeBtn.removeEventListener("click", onMinimize);
      }
    };
  }, [setOpen, widgetLoaded]);

  useEffect(() => {
    if (!widgetLoaded) return;
    let pollId = null;

    const getChatBtn = () =>
      document.querySelector(".five9-chat-button") ||
      document.querySelector("#five9-maximize-button") ||
      document.querySelector("[class*='five9'][class*='button']");

    const getMinimizeBtn = () =>
      document.querySelector("#five9-minimize-button") ||
      document.querySelector("[class*='five9'][class*='minimize']");

    if (open) {
      let attempts = 0;

      const tryOpen = () => {
        showFive9Widget();
        const chatBtn = getChatBtn();
        if (!chatBtn) return false;

        chatBtn.style.setProperty("display", "flex", "important");
        chatBtn.style.setProperty("visibility", "visible", "important");
        chatBtn.click();

        return true;
      };

      if (!tryOpen()) {
        pollId = window.setInterval(() => {
          attempts += 1;
          if (tryOpen() || attempts >= 40) {
            window.clearInterval(pollId);
            pollId = null;
          }
        }, 125);
      }
    } else {
      ensureFive9WidgetHidden();
      let attempts = 0;
      pollId = window.setInterval(() => {
        attempts += 1;
        const minimizeBtn = getMinimizeBtn();
        if (minimizeBtn) {
          minimizeBtn.click();
          window.clearInterval(pollId);
          pollId = null;
        } else if (attempts >= 20) {
          window.clearInterval(pollId);
          pollId = null;
        }
      }, 100);
    }

    return () => {
      if (!open) {
        ensureFive9WidgetHidden();
      }
      if (pollId) {
        window.clearInterval(pollId);
      }
    };
  }, [open, widgetLoaded]);

  return null;
};

Five9Widget.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  isSpeed: PropTypes.bool,
};

export default Five9Widget;
